// QBR Goals Scorecard — slide 6 wiring.
//
// Five FMX-standard goals are defined here as defaults. The CSM can
// override each goal's label and target value per project in the
// Generate step form. Current values auto-fill from the live metrics;
// statuses (HIT / ON TRACK / WATCH / AT RISK) are derived from
// (current, target, direction) with per-unit thresholds.
//
// Each goal carries:
//   id        — stable key; the metric lookup uses this
//   label     — display string, editable per project
//   target    — numeric target value, editable per project
//   unit      — 'days' or '%' (drives formatting + thresholds)
//   direction — 'higher' (more is better) or 'lower' (less is better)
//   targetPrefix — what to prepend to the target display ('< ' for
//                  "less than" targets, '' otherwise)
//   onTrack / watch — absolute-unit thresholds for the status bucket.
//                     A 5-point gap is meaningful for %s but a 0.5-day
//                     gap is meaningful for days, so the thresholds
//                     scale with the unit.

export const GOAL_DEFAULTS = [
  { id: 'response_time',  label: 'Reduce avg response time',   target: 1,  unit: 'days', direction: 'lower',  targetPrefix: '< ', onTrack: 0.5, watch: 1.5 },
  { id: 'pm_compliance',  label: 'Increase PM compliance',      target: 85, unit: '%',    direction: 'higher', targetPrefix: '',   onTrack: 5,   watch: 15  },
  { id: 'adoption',       label: 'Active user adoption',         target: 80, unit: '%',    direction: 'higher', targetPrefix: '',   onTrack: 5,   watch: 15  },
  { id: 'reactive_share', label: 'Reduce reactive WO share',     target: 50, unit: '%',    direction: 'lower',  targetPrefix: '< ', onTrack: 5,   watch: 15  },
  { id: 'mobile',         label: 'Mobile adoption',              target: 75, unit: '%',    direction: 'higher', targetPrefix: '',   onTrack: 5,   watch: 15  },
];

// Computes HIT / ON TRACK / WATCH / AT RISK from current vs target.
// Returns '—' when either value is missing or non-numeric.
export function computeGoalStatus(current, target, direction, onTrack, watch) {
  if (current == null || current === '' || target == null || target === '') return '—';
  const c = Number(current);
  const t = Number(target);
  if (!Number.isFinite(c) || !Number.isFinite(t)) return '—';
  const beat = direction === 'higher' ? c >= t : c <= t;
  if (beat) return 'HIT';
  const gap = Math.abs(c - t);
  if (gap <= onTrack) return 'ON TRACK';
  if (gap <= watch)   return 'WATCH';
  return 'AT RISK';
}

// "85%" / "< 1 day" / "0.8 days" / "—" — used for both target and
// current values. Pass the goal definition + the value.
export function formatGoalValue(value, unit, prefix = '') {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (unit === 'days') {
    const rounded = n >= 10 ? n.toFixed(0) : n.toFixed(1).replace(/\.0$/, '');
    return `${prefix}${rounded} day${Math.abs(n) === 1 ? '' : 's'}`;
  }
  return `${prefix}${n.toFixed(1).replace(/\.0$/, '')}%`;
}

// Merge the saved per-project goal overrides over GOAL_DEFAULTS.
// Returns an array of 5 goal definitions in canonical id order, each
// with the customer's label/target if they overrode it, defaults if
// not. Future schema changes (new goal ids, removed ids) survive
// gracefully — saved overrides for unknown ids are ignored.
export function mergeGoals(savedOverrides) {
  const byId = new Map(
    (savedOverrides || []).map(o => [o.id, o])
  );
  return GOAL_DEFAULTS.map(def => {
    const o = byId.get(def.id) || {};
    return {
      ...def,
      label:  o.label  != null && o.label !== ''  ? o.label  : def.label,
      target: o.target != null && o.target !== '' ? o.target : def.target,
    };
  });
}

// Extracts the current numeric value for a goal from the dataResult
// shape. Returns null when the metric isn't present (in which case the
// slide cell falls back to em-dash).
export function currentValueForGoal(goalId, dataResult) {
  const overall = dataResult?.overall?.values || dataResult?.values || {};
  const tenant  = dataResult?.tenant?.values  || {};
  switch (goalId) {
    case 'response_time':  return overall.response_time?.responseDays   ?? null;
    case 'pm_compliance':  return overall.pm_compliance?.onTimePct      ?? null;
    case 'adoption':       return (tenant.adoption || overall.adoption)?.active30Pct ?? null;
    case 'reactive_share': {
      const p = overall.planned_vs_reactive?.pct;
      return p != null ? 100 - p : null;
    }
    case 'mobile':         return (tenant.adoption || overall.adoption)?.mobilePct ?? null;
    default: return null;
  }
}
